import type {
  AdminLlmLogsResponseDto,
  AdminLlmPromptTemplateDetailDto,
  AdminLlmPromptTemplateSelectDto,
  AdminLlmPromptTemplateVersionActivateDto,
  AdminLlmPromptTemplateVersionCreateDto,
  AdminLlmPromptTemplatesSelectDto,
  LlmLogSelectDto,
  LlmModelSelectDto,
} from '@@types/api.types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './keys';
import { useGetCurrentUser } from './users.query';

export const useLlmModelsQueryOptions = () => {
  const { apiGet } = useApi();

  return queryOptions<LlmModelSelectDto[]>({
    queryKey: queryKeys.llm.getModels,
    queryFn: async () => {
      return apiGet<LlmModelSelectDto[]>({ path: '/v1/llm/models' });
    },
  });
};

type AdminLlmLogsQueryParams = {
  page?: number | null;
  limit?: number | null;
};

export const useAdminLlmLogsQuery = ({ page, limit }: AdminLlmLogsQueryParams) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminLlmLogsResponseDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryKey: queryKeys.llm.getAdminLogs({ page, limit }),
    queryFn: async () => {
      return apiGet<AdminLlmLogsResponseDto>({
        path: '/v1/admin/llm/logs',
        query: { page, limit },
      });
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  });
};

export const useAdminLlmLogQuery = (logId?: string, initialData?: LlmLogSelectDto) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();
  const enabled = isAdmin && Boolean(logId);

  return useQuery<LlmLogSelectDto, ApiErrorWrapper>({
    enabled,
    initialData,
    queryKey: enabled ? queryKeys.llm.getAdminLogById(logId as string) : queryKeys.placeholders.default,
    queryFn: async () => {
      if (!logId) {
        throw new Error('logId is required to fetch admin LLM log details.');
      }

      return apiGet<LlmLogSelectDto>({ path: `/v1/admin/llm/logs/${logId}` });
    },
  });
};

export const useAdminLlmPromptsQuery = () => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminLlmPromptTemplatesSelectDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryFn: async () => {
      return await apiGet<AdminLlmPromptTemplatesSelectDto>({
        path: '/v1/admin/llm/prompts',
      });
    },
    queryKey: queryKeys.llm.getAdminPrompts,
  });
};

export function buildAdminLlmPromptDetailPlaceholderData(params: {
  initialData?: AdminLlmPromptTemplateSelectDto;
  previousData?: AdminLlmPromptTemplateDetailDto;
}): AdminLlmPromptTemplateDetailDto | undefined {
  const { initialData, previousData } = params;

  if (previousData) {
    return previousData;
  }

  return buildPromptDetailInitialData(initialData);
}

function buildPromptDetailInitialData(
  initialData?: AdminLlmPromptTemplateSelectDto,
): AdminLlmPromptTemplateDetailDto | undefined {
  if (!initialData) {
    return undefined;
  }

  return {
    ...initialData,
    versions: [
      {
        isActive: initialData.version === initialData.activeVersion,
        updatedAt: initialData.updatedAt,
        updatedByUser: initialData.updatedByUser,
        updatedByUserId: initialData.updatedByUserId,
        version: initialData.version,
      },
    ],
  };
}

export function mergeAdminLlmPromptDetailCache(params: {
  cachedData?: AdminLlmPromptTemplateDetailDto;
  nextData: AdminLlmPromptTemplateDetailDto;
}): AdminLlmPromptTemplateDetailDto {
  const { cachedData, nextData } = params;

  if (!cachedData || cachedData.key !== nextData.key || cachedData.version === nextData.version) {
    return nextData;
  }

  return {
    ...cachedData,
    activeVersion: nextData.activeVersion,
    versions: nextData.versions,
  };
}

export const useAdminLlmPromptQuery = (params: {
  editing?: boolean;
  initialData?: AdminLlmPromptTemplateSelectDto;
  key?: string;
  version?: number;
}) => {
  const { editing = false, initialData, key, version } = params;
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();
  const enabled = isAdmin && Boolean(key);

  return useQuery<AdminLlmPromptTemplateDetailDto, ApiErrorWrapper>({
    enabled,
    placeholderData: (previousData) =>
      buildAdminLlmPromptDetailPlaceholderData({
        initialData,
        previousData,
      }),
    queryFn: async () => {
      if (!key) {
        throw new Error('Prompt key is required to fetch admin LLM prompt details.');
      }

      return await apiGet<AdminLlmPromptTemplateDetailDto>({
        path: `/v1/admin/llm/prompts/${key}`,
        query: version === undefined ? undefined : { version },
      });
    },
    queryKey: key ? queryKeys.llm.getAdminPromptByKey({ key, version }) : queryKeys.placeholders.default,
    refetchOnWindowFocus: !editing,
    staleTime: editing ? Number.POSITIVE_INFINITY : undefined,
  });
};

export const useAdminActivateLlmPromptTemplateVersionMutation = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    AdminLlmPromptTemplateDetailDto,
    ApiErrorWrapper,
    { dto: AdminLlmPromptTemplateVersionActivateDto; key: string }
  >({
    mutationFn: async (params) => {
      const { dto, key } = params;

      return await apiPost<AdminLlmPromptTemplateDetailDto>({
        body: dto,
        path: `/v1/admin/llm/prompts/${key}/activate`,
      });
    },
    onSuccess: (data, variables) => {
      void queryClient.setQueryData(
        queryKeys.llm.getAdminPromptByKey({
          key: variables.key,
          version: data.version,
        }),
        data,
      );
      void queryClient.setQueriesData<AdminLlmPromptTemplateDetailDto>(
        { queryKey: queryKeys.llm.getAdminPromptByKeyPrefix(variables.key) },
        (cachedData) =>
          mergeAdminLlmPromptDetailCache({
            cachedData,
            nextData: data,
          }),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.llm.getAdminPrompts });
    },
  });
};

export const useAdminCreateLlmPromptTemplateVersionMutation = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    AdminLlmPromptTemplateDetailDto,
    ApiErrorWrapper,
    { dto: AdminLlmPromptTemplateVersionCreateDto; key: string }
  >({
    mutationFn: async (params) => {
      const { dto, key } = params;

      return await apiPost<AdminLlmPromptTemplateDetailDto>({
        body: dto,
        path: `/v1/admin/llm/prompts/${key}/versions`,
      });
    },
    onSuccess: (data, variables) => {
      void queryClient.setQueryData(
        queryKeys.llm.getAdminPromptByKey({
          key: variables.key,
          version: data.version,
        }),
        data,
      );
      void queryClient.setQueriesData<AdminLlmPromptTemplateDetailDto>(
        { queryKey: queryKeys.llm.getAdminPromptByKeyPrefix(variables.key) },
        (cachedData) =>
          mergeAdminLlmPromptDetailCache({
            cachedData,
            nextData: data,
          }),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.llm.getAdminPrompts });
    },
  });
};

export const useAcceptLlmSuggestionMutation = () => {
  const { apiPatch } = useApi();

  return useMutation<{ success: boolean }, ApiErrorWrapper, string>({
    mutationFn: async (logId: string) => {
      return apiPatch<{ success: boolean }>({
        path: `/v1/llm/logs/${logId}/accept`,
      });
    },
  });
};

export const useRejectLlmSuggestionMutation = () => {
  const { apiPatch } = useApi();

  return useMutation<{ success: boolean }, ApiErrorWrapper, string>({
    mutationFn: async (logId: string) => {
      return apiPatch<{ success: boolean }>({
        path: `/v1/llm/logs/${logId}/reject`,
      });
    },
  });
};
